// Country/flag helpers shared by the landing page and admin pages.
// Pure data + functions (no JSX) so the landing bundle doesn't pull in admin code.

const COUNTRY_TO_ISO: Record<string, string> = {
  afghanistan: "AF", albania: "AL", algeria: "DZ", argentina: "AR",
  armenia: "AM", australia: "AU", austria: "AT", azerbaijan: "AZ",
  bangladesh: "BD", belarus: "BY", belgium: "BE", bolivia: "BO",
  "bosnia and herzegovina": "BA", brazil: "BR", bulgaria: "BG",
  cambodia: "KH", cameroon: "CM", canada: "CA", chile: "CL",
  china: "CN", colombia: "CO", "costa rica": "CR", croatia: "HR",
  cuba: "CU", cyprus: "CY", "czech republic": "CZ", czechia: "CZ",
  denmark: "DK", "dominican republic": "DO", ecuador: "EC",
  egypt: "EG", "el salvador": "SV", estonia: "EE", ethiopia: "ET",
  finland: "FI", france: "FR", georgia: "GE", germany: "DE",
  ghana: "GH", greece: "GR", guatemala: "GT", honduras: "HN",
  "hong kong": "HK", hungary: "HU", iceland: "IS", india: "IN",
  indonesia: "ID", iran: "IR", iraq: "IQ", ireland: "IE",
  israel: "IL", italy: "IT", jamaica: "JM", japan: "JP",
  jordan: "JO", kazakhstan: "KZ", kenya: "KE", "south korea": "KR",
  korea: "KR", kuwait: "KW", kyrgyzstan: "KG", latvia: "LV",
  lebanon: "LB", libya: "LY", lithuania: "LT", luxembourg: "LU",
  malaysia: "MY", malta: "MT", mexico: "MX", moldova: "MD",
  mongolia: "MN", montenegro: "ME", morocco: "MA", myanmar: "MM",
  nepal: "NP", netherlands: "NL", "new zealand": "NZ", nicaragua: "NI",
  nigeria: "NG", "north macedonia": "MK", norway: "NO", oman: "OM",
  pakistan: "PK", panama: "PA", paraguay: "PY", peru: "PE",
  philippines: "PH", poland: "PL", portugal: "PT", qatar: "QA",
  romania: "RO", russia: "RU", "saudi arabia": "SA", senegal: "SN",
  serbia: "RS", singapore: "SG", slovakia: "SK", slovenia: "SI",
  "south africa": "ZA", spain: "ES", "sri lanka": "LK", sudan: "SD",
  sweden: "SE", switzerland: "CH", syria: "SY", taiwan: "TW",
  tajikistan: "TJ", tanzania: "TZ", thailand: "TH", tunisia: "TN",
  turkey: "TR", turkmenistan: "TM", uganda: "UG", ukraine: "UA",
  "united arab emirates": "AE", uae: "AE",
  "united kingdom": "GB", uk: "GB",
  "united states": "US", "united states of america": "US", usa: "US",
  uruguay: "UY", uzbekistan: "UZ", venezuela: "VE", vietnam: "VN",
  yemen: "YE", zambia: "ZM", zimbabwe: "ZW",
};

// Canonical display names for the notify-form country dropdown, derived from
// COUNTRY_TO_ISO keys with ISO-collision aliases collapsed to one preferred
// spelling (e.g. "United Kingdom" not "uk", "South Korea" not "korea").
// Title-cased, deduped, alphabetical. Hand-curated to keep the preferred name
// per ISO rather than re-deriving collision rules at runtime.
export const COUNTRY_NAMES: string[] = [
  "Afghanistan", "Albania", "Algeria", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bangladesh", "Belarus", "Belgium", "Bolivia",
  "Bosnia and Herzegovina", "Brazil", "Bulgaria", "Cambodia", "Cameroon",
  "Canada", "Chile", "China", "Colombia", "Costa Rica", "Croatia", "Cuba",
  "Cyprus", "Czech Republic", "Denmark", "Dominican Republic", "Ecuador",
  "Egypt", "El Salvador", "Estonia", "Ethiopia", "Finland", "France",
  "Georgia", "Germany", "Ghana", "Greece", "Guatemala", "Honduras",
  "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq",
  "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan",
  "Kenya", "Kuwait", "Kyrgyzstan", "Latvia", "Lebanon", "Libya", "Lithuania",
  "Luxembourg", "Malaysia", "Malta", "Mexico", "Moldova", "Mongolia",
  "Montenegro", "Morocco", "Myanmar", "Nepal", "Netherlands", "New Zealand",
  "Nicaragua", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan",
  "Panama", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar",
  "Romania", "Russia", "Saudi Arabia", "Senegal", "Serbia", "Singapore",
  "Slovakia", "Slovenia", "South Africa", "South Korea", "Spain", "Sri Lanka",
  "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan",
  "Tanzania", "Thailand", "Tunisia", "Turkey", "Turkmenistan", "Uganda",
  "Ukraine", "United Arab Emirates", "United Kingdom", "United States",
  "Uruguay", "Uzbekistan", "Venezuela", "Vietnam", "Yemen", "Zambia",
  "Zimbabwe",
];

export function countryToFlag(countryName: string): string {
  const iso = COUNTRY_TO_ISO[countryName.toLowerCase().trim()];
  if (!iso) return "";
  return String.fromCodePoint(
    ...iso.split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65),
  );
}

// ISO code → canonical country name (reverse of COUNTRY_TO_ISO). COUNTRY_NAMES
// holds the preferred spelling per ISO, so aliases like "uk" never win over
// "United Kingdom". Lets us map a browser locale region (en-GB → GB) to a value.
const ISO_TO_NAME: Record<string, string> = (() => {
  const out: Record<string, string> = {};
  for (const name of COUNTRY_NAMES) {
    const iso = COUNTRY_TO_ISO[name.toLowerCase()];
    if (iso && !out[iso]) out[iso] = name;
  }
  return out;
})();

/** Canonical country name for an ISO-3166 alpha-2 code, or "" if unknown. */
export function isoToCountryName(iso: string): string {
  return ISO_TO_NAME[iso.toUpperCase().trim()] ?? "";
}

/** ISO-3166 alpha-2 code for a country name (e.g. "United Kingdom" -> "GB"),
    or "" if unknown. Used for the compact city line on mobile result cards. */
export function countryToCode(country: string | null | undefined): string {
  if (!country) return "";
  return COUNTRY_TO_ISO[country.toLowerCase().trim()] ?? "";
}
